// src/pages/admin/AdminDashboard.jsx
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { adminDashboardApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardHeader, CardBody, StatusBadge, PageSpinner } from '@/components/common/ui'
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts'
import {
  TrendingUp, Users, ShoppingBag, Package, DollarSign,
  AlertTriangle, Scale, Activity, ChevronRight, Clock,
  FileText, Truck, Shield, Star, UserCheck, UserPlus,
  ArrowUpRight, ArrowDownRight,
} from 'lucide-react'

const PIE_COLORS = ['#ec4899', '#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#f97316', '#ef4444', '#6366f1']

export default function AdminDashboard() {
  const { format } = useCurrency()

  const { data: res, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => adminDashboardApi.index(),
    refetchInterval: 120_000,
  })

  if (isLoading) return <PageSpinner />

  const d = res?.data?.data || res?.data || {}
  const overview       = d.overview || {}
  const gmvTrend       = d.gmv_trend || []
  const dailyRevenue   = d.daily_revenue || []
  const orderStats     = d.order_stats || {}
  const statusBreak    = d.status_breakdown || {}
  const pending        = d.pending_actions || {}
  const users          = d.users || {}
  const buyerCohort    = d.buyer_cohort || []
  const recentOrders   = d.recent_orders || []
  const topSuppliers   = d.top_suppliers || []
  const topProducts    = d.top_products || []
  const sampleCounts   = d.sample_counts || {}
  const rfqCounts      = d.rfq_counts || {}
  const pendingSupps   = d.pending_suppliers || []

  const totalPendingActions = (pending.products_review || 0) + (pending.supplier_kyc || 0) +
    (pending.sla_breached || 0) + (pending.open_disputes || 0) + (pending.open_fraud || 0)

  // Status breakdown for pie chart
  const statusData = Object.entries(statusBreak)
    .filter(([, v]) => v > 0)
    .map(([status, count]) => ({ name: status.replace(/_/g, ' '), value: count }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display font-black text-2xl mb-1">Admin Console</h1>
        <p className="text-sm text-muted">Platform overview at a glance</p>
      </div>

      {/* KPI Cards Row 1 — Revenue */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="GMV Today"
          value={format(overview.gmv_today || 0)}
          sub={`${orderStats.today || 0} orders`}
          icon={DollarSign}
          color="pink"
        />
        <KpiCard
          label="GMV This Month"
          value={format(overview.gmv_month || 0)}
          sub={overview.gmv_growth_rate != null ? (
            <span className={overview.gmv_growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}>
              {overview.gmv_growth_rate >= 0 ? <ArrowUpRight size={10} className="inline" /> : <ArrowDownRight size={10} className="inline" />}
              {' '}{Math.abs(overview.gmv_growth_rate)}% vs last month
            </span>
          ) : `${orderStats.this_month || 0} orders`}
          icon={TrendingUp}
          color="green"
        />
        <KpiCard
          label="Avg Order Value"
          value={overview.avg_order_value ? format(overview.avg_order_value) : '—'}
          sub={`${overview.repeat_buyer_rate || 0}% repeat buyers`}
          icon={ShoppingBag}
          color="blue"
        />
        <KpiCard
          label="Action Required"
          value={totalPendingActions}
          sub={`${pending.sla_breached || 0} SLA breached`}
          icon={AlertTriangle}
          color="amber"
          to="/admin/orders"
        />
      </div>

      {/* KPI Cards Row 2 — Users & Activity */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Buyers"
          value={users.total_buyers || 0}
          sub={`+${users.new_buyers_month || 0} this month`}
          icon={Users}
          color="indigo"
          to="/admin/users"
        />
        <KpiCard
          label="Suppliers"
          value={`${users.approved_suppliers || 0} / ${users.total_suppliers || 0}`}
          sub={`${pending.supplier_kyc || 0} pending KYC`}
          icon={UserCheck}
          color="teal"
          to="/admin/users"
        />
        <KpiCard
          label="Products"
          value={overview.products_live || 0}
          sub={`${pending.products_review || 0} pending review`}
          icon={Package}
          color="purple"
          to="/admin/products"
        />
        <KpiCard
          label="Open Disputes"
          value={pending.open_disputes || 0}
          sub={`${pending.open_fraud || 0} fraud flags`}
          icon={Scale}
          color="orange"
          to="/admin/disputes"
        />
      </div>

      {/* Pending Actions Bar */}
      {totalPendingActions > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardBody>
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle size={16} className="text-amber-600" />
              <span className="font-bold text-sm text-amber-800">Pending Actions ({totalPendingActions})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Product Reviews', count: pending.products_review, to: '/admin/products', icon: Package },
                { label: 'Supplier KYC', count: pending.supplier_kyc, to: '/admin/kyc', icon: Shield },
                { label: 'SLA Breaches', count: pending.sla_breached, to: '/admin/orders', icon: Clock },
                { label: 'Disputes', count: pending.open_disputes, to: '/admin/disputes', icon: Scale },
                { label: 'Fraud Flags', count: pending.open_fraud, to: '/admin/fraud', icon: AlertTriangle },
                { label: 'Payouts', count: pending.pending_payouts, to: '/admin/payouts', icon: DollarSign },
              ].filter(a => a.count > 0).map(a => (
                <Link key={a.label} to={a.to}
                  className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2 hover:border-pink transition-colors">
                  <a.icon size={14} className="text-amber-600" />
                  <div>
                    <p className="text-lg font-extrabold text-gray-900 leading-none">{a.count}</p>
                    <p className="text-[10px] text-muted font-bold uppercase">{a.label}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Revenue Chart + Order Status */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Daily Revenue */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <h2 className="font-bold text-sm">Revenue (Last 30 Days)</h2>
          </CardHeader>
          <CardBody>
            {dailyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyRevenue}>
                  <defs>
                    <linearGradient id="adminRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} axisLine={false} tickLine={false}
                    tickFormatter={v => new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']}
                    labelFormatter={v => new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}
                    labelStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#ec4899" fill="url(#adminRevGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-10 text-muted text-sm">No revenue data yet</div>
            )}
          </CardBody>
        </Card>

        {/* Order Status Breakdown */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-bold text-sm">Order Status</h2>
          </CardHeader>
          <CardBody>
            {statusData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                      {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1">
                  {statusData.map((s, i) => (
                    <div key={s.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="capitalize">{s.name}</span>
                      </span>
                      <span className="font-bold">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-muted text-sm">No orders yet</div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Monthly GMV Trend + New Buyers */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* GMV Trend */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm">Monthly GMV Trend</h2>
            <Link to="/admin/analytics" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              Full Analytics <ChevronRight size={12} />
            </Link>
          </CardHeader>
          <CardBody>
            {gmvTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={gmvTrend}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'GMV']} labelStyle={{ fontSize: 11 }} />
                  <Bar dataKey="gmv" fill="#ec4899" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-muted text-sm">No data</div>
            )}
          </CardBody>
        </Card>

        {/* Buyer Cohort */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <UserPlus size={14} className="text-indigo-500" /> New Buyer Registrations
            </h2>
          </CardHeader>
          <CardBody>
            {buyerCohort.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={buyerCohort}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip labelStyle={{ fontSize: 11 }} />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="New Buyers" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-muted text-sm">No data</div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Recent Orders + Top Suppliers */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Recent Orders */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <h2 className="font-bold text-sm">Recent Orders</h2>
            <Link to="/admin/orders" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {recentOrders.length === 0 ? (
            <CardBody><div className="text-center py-8 text-muted text-sm">No orders yet</div></CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {recentOrders.map(order => (
                <Link key={order.id} to={`/admin/orders/${order.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-pink/10 flex items-center justify-center flex-shrink-0">
                    <Package size={16} className="text-pink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">#{order.order_number}</p>
                    <p className="text-[11px] text-muted">
                      {order.buyer?.name} → {order.supplier?.business_name || order.supplier?.name}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold">{format(order.total_amount)}</p>
                    <StatusBadge status={order.status} />
                  </div>
                </Link>
              ))}
            </CardBody>
          )}
        </Card>

        {/* Top Suppliers */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Star size={14} className="text-amber-500" /> Top Suppliers
            </h2>
          </CardHeader>
          {topSuppliers.length === 0 ? (
            <CardBody><div className="text-center py-8 text-muted text-sm">No data</div></CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {topSuppliers.map((s, i) => (
                <div key={s.supplier_id || i} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{s.name}</p>
                    <p className="text-[11px] text-muted">{s.orders} orders</p>
                  </div>
                  <p className="text-sm font-bold flex-shrink-0">{format(s.gmv)}</p>
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      {/* Top Products + Pending Suppliers */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <TrendingUp size={14} className="text-green-500" /> Top Products
            </h2>
          </CardHeader>
          {topProducts.length === 0 ? (
            <CardBody><div className="text-center py-8 text-muted text-sm">No sales data</div></CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {topProducts.map((p, i) => (
                <div key={p.product_id || i} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{p.product_name}</p>
                    <p className="text-[11px] text-muted">{p.orders} orders · {p.metres}m</p>
                  </div>
                  <p className="text-sm font-bold flex-shrink-0">{format(p.revenue)}</p>
                </div>
              ))}
            </CardBody>
          )}
        </Card>

        {/* Pending Supplier Approvals */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Shield size={14} className="text-purple-500" /> Pending Supplier KYC
            </h2>
            <Link to="/admin/kyc" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              Review all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {pendingSupps.length === 0 ? (
            <CardBody><div className="text-center py-8 text-muted text-sm">No pending approvals</div></CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {pendingSupps.map(s => (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <UserCheck size={16} className="text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{s.business_name || s.name}</p>
                    <p className="text-[11px] text-muted">{s.email}</p>
                  </div>
                  <span className="text-[10px] text-muted">
                    {new Date(s.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      {/* Quick Stats Row — Samples & RFQs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Sample Requests"
          value={sampleCounts.total || 0}
          sub={`${sampleCounts.pending || 0} pending`}
          icon={Truck}
          color="indigo"
        />
        <KpiCard
          label="Samples This Month"
          value={sampleCounts.this_month || 0}
          sub="New requests"
          icon={Truck}
          color="teal"
        />
        <KpiCard
          label="Total RFQs"
          value={rfqCounts.total || 0}
          sub={`${rfqCounts.open || 0} open`}
          icon={FileText}
          color="purple"
        />
        <KpiCard
          label="RFQs This Month"
          value={rfqCounts.this_month || 0}
          sub="New RFQs"
          icon={FileText}
          color="orange"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { to: '/admin/orders',    label: 'Orders',      icon: ShoppingBag },
          { to: '/admin/products',  label: 'Products',    icon: Package },
          { to: '/admin/users',     label: 'Users',       icon: Users },
          { to: '/admin/analytics', label: 'Analytics',   icon: TrendingUp },
          { to: '/admin/disputes',  label: 'Disputes',    icon: Scale },
          { to: '/admin/payouts',   label: 'Payouts',     icon: DollarSign },
        ].map(a => (
          <Link key={a.to} to={a.to}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-border rounded-2xl hover:border-pink hover:shadow-card-md transition-all text-center">
            <a.icon size={20} className="text-pink" />
            <span className="text-xs font-bold">{a.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

// ── Reusable KPI Card ──────────────────────────────────────────────────────────
const COLORS = {
  pink:   'bg-pink/10 text-pink',
  blue:   'bg-blue-100 text-blue-600',
  green:  'bg-green-100 text-green-600',
  amber:  'bg-amber-100 text-amber-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal:   'bg-teal-100 text-teal-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
}

function KpiCard({ label, value, sub, icon: Icon, color = 'pink', to }) {
  const Wrapper = to ? Link : 'div'
  const wrapperProps = to ? { to } : {}
  return (
    <Wrapper {...wrapperProps}
      className={`bg-white border border-border rounded-xl p-4 ${to ? 'hover:border-pink hover:shadow-card-md transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${COLORS[color]}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-[11px] text-muted font-bold uppercase tracking-wider mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-muted mt-0.5">{typeof sub === 'string' ? sub : sub}</p>}
    </Wrapper>
  )
}
